import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatShoppingDate, listsApi, shoppingDateInput, type ShoppingSummary } from '../lib/domain';
import { ProblemItems } from '../components/ProblemItems';

type Detail = Awaited<ReturnType<typeof listsApi.detail>>['data']['list'];

// Abschlussübersicht einer Liste (auch Ziel der Benachrichtigung "Einkauf abgeschlossen").
export function ListSummaryPage() {
  const { id } = useParams<{ id: string }>();
  const [list, setList] = useState<Detail | null>(null);
  const [summary, setSummary] = useState<ShoppingSummary | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id) return;
    listsApi
      .detail(id)
      .then(async (d) => {
        setList(d.data.list);
        setSummary((await listsApi.summary(id, d.data.list)).summary);
      })
      .catch(() => setFailed(true));
  }, [id]);

  if (failed) return <p className="error-text">Die Übersicht konnte nicht geladen werden.</p>;
  if (!list || !summary) return <p>Lade…</p>;

  const scope = list.ownerGroupId ? `group:${list.ownerGroupId}` : list.ownerUserId ? `user:${list.ownerUserId}` : '';
  const date = shoppingDateInput(list.shoppingDate);
  const isArchived = list.status === 'ARCHIVED';

  return (
    <div className="stack">
      <div className="row between">
        <h1>Einkaufsübersicht: {list.name || 'Einkaufsliste'}</h1>
        <span className={`badge ${isArchived ? 'ok' : 'warn'}`}>{isArchived ? 'Abgeschlossen' : 'Noch offen'}</span>
      </div>
      <p className="muted">
        {list.storeBranch ? `${list.storeBranch.chain.name} - ${list.storeBranch.name}` : 'Ohne Filiale'}
        {date && ` · Einkaufsdatum ${formatShoppingDate(date)}`}
      </p>

      <div className="card row between">
        <div>
          <strong>{Number(summary.totalAmount).toFixed(2)} €</strong>
          <div className="muted">Gesamtsumme</div>
        </div>
        <div>
          <div>{summary.boughtCount} gekauft</div>
          <div className="muted">{summary.unavailableCount} nicht erhältlich</div>
        </div>
        {summary.missingPriceCount > 0 && <span className="badge warn">{summary.missingPriceCount} fehlende Preise</span>}
      </div>

      <h2>Nicht (vollständig) erhältliche Artikel</h2>
      <ProblemItems items={summary.problemItems} scope={scope} />

      <div className="row">
        <Link to="/lists">← Zu den Listen</Link>
        <Link to={`/lists/${list.id}`}>Zur Liste</Link>
        {date && scope.startsWith('group:') && <Link to={`/days/${date}?scope=${encodeURIComponent(scope)}`}>Tagesübersicht {formatShoppingDate(date)}</Link>}
      </div>
    </div>
  );
}
